import { useSearchParams } from 'react-router';
import AnalyticsV2 from './analytics-v2';
import { BrandHealthDashboard } from '@/modules/brand-health';
import { useSentinelDashboardStatus } from '@/hooks/use-sentinel-dashboard-enabled';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';

type Dashboard = 'analytics' | 'brand-health';

const DASHBOARDS: { value: Dashboard; label: string; description: string }[] = [
  { value: 'analytics', label: 'Analytics', description: 'Messages and tickets Kim handled' },
  { value: 'brand-health', label: 'Brand health', description: 'Customer sentiment and ticket trends' },
];

/**
 * Takes the "Dashboard" subtitle's place under the greeting, so switching dashboards reads as
 * part of the heading rather than as another control in the filter row.
 */
function DashboardSwitcher({ value, onChange }: { value: Dashboard; onChange: (next: Dashboard) => void }) {
  const current = DASHBOARDS.find((d) => d.value === value)!;
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Dashboard)}>
      <SelectTrigger
        aria-label='Choose dashboard'
        className='-ml-2 h-6! w-auto gap-1 border-0 bg-transparent px-2 py-0 text-sm text-gray-700 shadow-none hover:bg-gray-100 hover:text-ink focus:ring-0 focus-visible:ring-0 data-[state=open]:bg-gray-100'
      >
        {current.label} dashboard
      </SelectTrigger>
      <SelectContent align='start' className='min-w-[240px]'>
        {DASHBOARDS.map((d) => (
          <SelectItem key={d.value} value={d.value} className='py-2'>
            <span className='flex flex-col gap-0.5'>
              <span className='text-[13px] font-medium text-ink'>{d.label}</span>
              <span className='text-[11px] text-gray-700'>{d.description}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Analytics is always the landing dashboard. Accounts (or brands) with the Sentinel workspace on
 * also get Brand Health, reached from the switcher and kept in `?dashboard=` so it survives a
 * refresh and can be linked to.
 *
 * Only a request FOR Brand Health waits on the Sentinel gate: rendering it before the answer
 * would flash it for accounts that turn out not to have it. Analytics needs no answer, so it
 * renders at once and the switcher appears when the gate does. A gate failure falls back to
 * Analytics, the pre-Sentinel view.
 */
export default function Overview() {
  const status = useSentinelDashboardStatus();
  const [params, setParams] = useSearchParams();
  const requested: Dashboard = params.get('dashboard') === 'brand-health' ? 'brand-health' : 'analytics';

  const select = (next: Dashboard) =>
    setParams((prev) => {
      if (next === 'analytics') prev.delete('dashboard');
      else prev.set('dashboard', next);
      return prev;
    });

  if (requested === 'brand-health' && status.enabled === null && !status.isError) {
    return (
      <div
        role='status'
        aria-label='Loading overview'
        className='mx-auto h-full w-full max-w-[1440px] animate-pulse px-6 py-5'
      >
        <div className='h-12 w-full rounded-lg bg-gray-100' />
        <div className='mt-6 h-52 w-full rounded-xl bg-gray-100' />
      </div>
    );
  }

  const active: Dashboard = status.enabled === true ? requested : 'analytics';
  const switcher = status.enabled === true ? <DashboardSwitcher value={active} onChange={select} /> : undefined;

  return active === 'brand-health' ? <BrandHealthDashboard switcher={switcher} /> : <AnalyticsV2 switcher={switcher} />;
}
